import {
    Decimal,
    ReceiptAttachmentProcessRes,
    Ulid,
} from "@kase/my-marathon-communication";
import { Signal, signal } from "@preact/signals";
import { upsertOne } from "~/utils";
import { AttachmentProcess, FiguresMap, OcrSliceParams } from "./interfaces";

const IDLE_LOADING = signal(false);

export const makeOCR = ({ connection, mapCode }: OcrSliceParams) => {
    const items = signal<AttachmentProcess[]>([]);
    const loadingStore: Record<string, Signal<boolean>> = {};
    const loadedState: Record<string, boolean> = {};
    const globalLoading = signal(false);
    let globalCounter = 0;

    const view = (
        item: ReceiptAttachmentProcessRes,
        blobId: string,
        attachmentId: string,
    ): AttachmentProcess => {
        const figuresMap: FiguresMap = {};
        const pages = item.pages.map((page) => ({
            width: Number(page.width),
            height: Number(page.height),
        }));
        const type = mapCode(item.spsc);

        const n = (v: { base: bigint; neg_precision: number }) =>
            Decimal.from(v).toNumber();

        if (item.date && item.date.score && n(item.date.score) > 0) {
            figuresMap.date = {
                type: "date",
                score: n(item.date.score),
                label: "Date",
                boundaries: item.date.bounding_region ?? [],
                value: new Date(Number(item.date.value)),
            };
        }

        if (
            item.total &&
            item.total.score &&
            item.total.value &&
            n(item.total.score) > 0
        ) {
            figuresMap.total = {
                type: "number",
                score: n(item.total.score),
                label: "Total",
                boundaries: item.total.bounding_region ?? [],
                value: n(item.total.value),
            };
        }

        if (
            item.vat &&
            item.vat.score &&
            item.vat.value &&
            n(item.vat.score) > 0
        ) {
            figuresMap.vat = {
                type: "number",
                score: n(item.vat.score),
                label: "VAT",
                boundaries: item.vat.bounding_region ?? [],
                value: n(item.vat.value),
            };
        }

        if (
            item.currency &&
            item.currency.score &&
            n(item.currency.score) > 0
        ) {
            figuresMap.currency = {
                type: "text",
                score: n(item.currency.score),
                label: "Currency",
                boundaries: item.currency.bounding_region ?? [],
                value: item.currency.value,
            };
        }

        if (
            item.category &&
            item.category.score &&
            n(item.category.score) > 0
        ) {
            figuresMap.category = {
                type: "text",
                score: n(item.category.score),
                label: "Category",
                boundaries: item.category.bounding_region ?? [],
                value: item.category.value,
            };
        }

        const figures = Object.values(figuresMap);

        return {
            blobId,
            attachmentId,
            figures,
            figuresMap,
            pages,
            type,
            raw: item,
        };
    };

    const process = async (blobId: Readonly<Ulid>, attachmentId: string) => {
        const con = connection.value;
        const id = attachmentId;

        if (
            items.peek().some((i) => i.attachmentId === id) ||
            loadingStore[id]?.peek() ||
            loadedState[id]
        ) {
            return;
        }

        if (loadingStore[id]) {
            loadingStore[id].value = true;
        } else {
            loadingStore[id] = signal(true);
        }

        ++globalCounter;
        globalLoading.value = true;

        let ended = false;

        const onEnd = () => {
            loadingStore[id].value = false;
            loadedState[id] = true;
            --globalCounter;

            if (globalCounter <= 0) {
                globalLoading.value = false;
            }
        };

        try {
            const res = con.expense.receipt.processAttachment({
                blob_id: blobId,
            });
            const cached = await res.cached;

            if (cached) {
                items.value = upsertOne(
                    items.peek().slice(),
                    view(cached, blobId.toString(), attachmentId),
                    "attachmentId",
                );
                onEnd();
                ended = true;
            }

            await res.current.then((item) => {
                if (item) {
                    items.value = upsertOne(
                        items.peek().slice(),
                        view(item, blobId.toString(), attachmentId),
                        "attachmentId",
                    );

                    if (!ended) {
                        onEnd();
                    }
                }
            });
        } catch (err: unknown) {
            console.error("[ocr] process error", err);

            if (!ended) {
                onEnd();
            }
        }
    };

    const getFor = (attachmentId: string | undefined) =>
        items.value.find(
            (i) => attachmentId && i.attachmentId === attachmentId,
        ) ?? null;

    const combined = (attachmentIds: string[]) => {
        void items.value;
        void globalLoading.value;

        const records = attachmentIds
            .map((id) => getFor(id))
            .filter((i) => i !== null);
        const loading = records.some(
            (i) => loadingStore[i.attachmentId]?.peek() ?? false,
        );
        const empty =
            records.length !== attachmentIds.length ||
            attachmentIds.length === 0;
        const duplicate = records.some((i) => i.raw.duplicate);

        return {
            empty,
            duplicate,
            loading,
        };
    };

    const loading = (attachmentId: string | undefined) => {
        if (!attachmentId) {
            return IDLE_LOADING;
        }

        const id = attachmentId;

        if (!(id in loadingStore)) {
            loadingStore[id] = signal(false);
        }

        return loadingStore[id];
    };

    const anyLoading = (attachmentIds: string[]) => {
        void globalLoading.value;

        return attachmentIds.some((id) => loadingStore[id]?.peek() ?? false);
    };

    const forget = (attachmentId: string) => {
        items.value = items
            .peek()
            .filter((i) => i.attachmentId !== attachmentId);
        delete loadingStore[attachmentId];
        delete loadedState[attachmentId];
    };

    return { items, process, loading, anyLoading, getFor, combined, forget };
};
